import React from 'react';
import { CaretDownIcon } from '@/components/Icons';
import cn from '@/utils/cn';

const NavigationClass = cn([
  'flex',
  'items-center',
  'justify-center',
  'w-6',
  'h-6',
  'rounded',
  'border',
  'border-emerge-navy-4',
  'text-emerge-navy-2',
  'hover:bg-emerge-base-2'
]);

const NavigationDisabledClass = cn([
  'opacity-30',
  'border-aux-disablegray-2',
  'text-aux-disablegray-2',
  'cursor-not-allowed',
  'hover:bg-transparent'
]);

const PageNumberClass = cn([
  'cursor-pointer',
  'flex',
  'items-center',
  'justify-center',
  'w-6',
  'h-6',
  'rounded',
  'text-xs',
  'font-medium',
  'text-emerge-navy-2',
  'hover:bg-emerge-base-2'
]);

const ActivePageClass = cn([
  'border-b-2',
  'border-emerge-teal',
  'rounded-none',
  'text-emerge-teal',
  'hover:bg-transparent'
]);

const Ellipsis = (side: string) => (
  <li key={`${side}-ellipsis`}>
    <div className={cn(PageNumberClass, 'cursor-default hover:bg-transparent')}>...</div>
  </li>
);

interface DataTableFooterNewProps {
  onPageChange: (p: number) => void;
  pageSize: number;
  totalItemsCount: number;
  currentPage: number;
  currentPageItemsCount: number;
  itemLabel?: string;
  isFloating: boolean;
  /**
   * Background behind the sticky footer while floating - covers the row content scrolling
   * underneath it. Defaults to white; override when the surrounding page isn't white (e.g. Batch
   * Overview's bg-aux-lightgray), so the footer doesn't show a mismatched white patch.
   */
  floatingBackgroundClassName?: string;
}

const DataTableFooterNew: React.FC<DataTableFooterNewProps> = ({
  pageSize,
  totalItemsCount,
  currentPage,
  onPageChange,
  itemLabel = 'Records',
  isFloating,
  floatingBackgroundClassName = 'bg-white'
}) => {
  const numberOfPages = Math.ceil(totalItemsCount / pageSize);
  const hasMoreThanMaximumPage = totalItemsCount > pageSize;
  const isActive = (index: number) => currentPage === index;
  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= numberOfPages;

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= numberOfPages) {
      onPageChange(p);
    }
  };

  const renderPageButton = (page: number) => (
    <li key={page}>
      <button
        type="button"
        onClick={() => onPageChange(page)}
        aria-current={isActive(page) ? 'page' : undefined}
        className={cn(PageNumberClass, isActive(page) && ActivePageClass)}>
        {page}
      </button>
    </li>
  );

  const renderPageItems = () => {
    const items = [
      <li key="prev">
        <button
          type="button"
          aria-label="Previous page"
          disabled={isPrevDisabled}
          onClick={() => handlePageChange(currentPage - 1)}
          className={cn(NavigationClass, isPrevDisabled && NavigationDisabledClass)}>
          <CaretDownIcon size="xs" className="rotate-90" />
        </button>
      </li>
    ];

    if (numberOfPages <= 5) {
      for (let i = 1; i <= numberOfPages; i++) {
        items.push(renderPageButton(i));
      }
    } else {
      const numPage = 5;
      const leftside = currentPage - numPage / 2 > 1;
      const rightside = currentPage + numPage / 2 < numberOfPages;

      if (leftside) items.push(Ellipsis('left'));

      const start = Math.max(1, Math.round(currentPage - numPage / 2));
      const end = Math.min(numberOfPages, Math.round(currentPage + numPage / 2));
      for (let i = start; i < end; i++) {
        items.push(renderPageButton(i));
      }

      if (rightside) items.push(Ellipsis('right'));
      items.push(renderPageButton(numberOfPages));
    }

    items.push(
      <li key="next">
        <button
          type="button"
          aria-label="Next page"
          disabled={isNextDisabled}
          onClick={() => handlePageChange(currentPage + 1)}
          className={cn(NavigationClass, isNextDisabled && NavigationDisabledClass)}>
          <CaretDownIcon size="xs" className="-rotate-90" />
        </button>
      </li>
    );

    return items;
  };

  return (
    <div className={cn(isFloating && ['sticky bottom-0 z-10 pb-4', floatingBackgroundClassName])}>
      <div
        className={cn(
          'flex items-center justify-between gap-x-4 px-4 py-4 bg-emerge-base-3',
          'border-l border-r border-b border-emerge-navy-4 rounded-b-2xl',
          isFloating && 'shadow-footer'
        )}>
        <div className="flex items-center gap-x-3">
          <span className="text-xs font-medium text-emerge-navy-2">
            {totalItemsCount} {itemLabel}
          </span>

          {hasMoreThanMaximumPage && (
            <nav>
              <ul className="flex items-center gap-x-1">{renderPageItems()}</ul>
            </nav>
          )}
        </div>

        <div />
      </div>
    </div>
  );
};

export default DataTableFooterNew;
